import React, { useState } from 'react';
import { FiAlertTriangle, FiX, FiXCircle } from 'react-icons/fi';
import SummaryApi from '../../common';
import { useNotification } from '../../context/NotificationContext';
import Modal from '../../components/Modal';

const RejectReturnModal = ({ isOpen, onClose, returnData, onRejected }) => {
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Format date
  const formatDate = (dateString) => {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Handle reject button click
  const handleRejectClick = () => {
    if (!rejectionReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    setShowConfirmation(true);
  };

  // Handle final rejection confirmation
  const handleFinalRejection = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${SummaryApi.rejectReturnedInventory.url}/${returnData.id}`, {
        method: SummaryApi.rejectReturnedInventory.method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rejectionReason: rejectionReason.trim()
        })
      });

      const data = await response.json();

      if (data.success) {
        showNotification('success', 'Inventory return rejected successfully');
        if (onRejected) onRejected();
        onClose();
        // Reset form
        setRejectionReason('');
        setShowConfirmation(false);
      } else {
        setError(data.message || 'Failed to reject inventory return');
        setShowConfirmation(false);
      }
    } catch (err) {
      setError('Server error. Please try again later.');
      console.error('Error rejecting inventory return:', err);
      setShowConfirmation(false);
    } finally {
      setLoading(false);
    }
  };

  // Reset modal state when closing
  const handleClose = () => {
    setRejectionReason('');
    setError(null);
    setShowConfirmation(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Reject Returned Inventory"
      size="lg"
    >
      {!showConfirmation ? (
        // First screen - Review and enter rejection reason
        <div className="p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md">
              {error}
            </div>
          )}

          <div className="mb-4 flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Inventory Returned by {returnData?.technician.name}
              </h3>
              <p className="text-sm text-gray-500">
                Returned on {formatDate(returnData?.returnedAt)}
              </p>
            </div>
            <div className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full flex items-center">
              <FiAlertTriangle className="mr-1" />
              Pending
            </div>
          </div>

          <div className="mb-6">
            <h4 className="font-medium text-gray-700 mb-2">Items to be rejected:</h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial Number</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {returnData?.items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.type === 'serialized-product'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {item.type === 'serialized-product' ? 'Serialized' : 'Generic'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-500">
                        {item.quantity} {item.unit || 'units'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {item.serialNumber ? (
                          <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-mono">
                            {item.serialNumber}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Rejection Reason Input */}
          <div className="mb-6">
            <label htmlFor="rejectionReason" className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Rejection <span className="text-red-500">*</span>
            </label>
            <textarea
              id="rejectionReason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Please provide a clear reason for rejecting this return request..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
              rows="4"
              maxLength="500"
            />
            <p className="mt-1 text-sm text-gray-500">
              {rejectionReason.length}/500 characters
            </p>
          </div>

          <div className="border-t pt-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRejectClick}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none flex items-center"
            >
              <FiXCircle className="mr-2" />
              Reject Return
            </button>
          </div>
        </div>
      ) : (
        // Second screen - Final confirmation
        <div className="p-4">
          <div className="flex flex-col items-center justify-center p-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <FiAlertTriangle className="w-8 h-8 text-red-500" />
            </div>

            <h3 className="text-lg font-medium text-center text-gray-900 mb-2">
              Confirm Rejection
            </h3>

            <p className="text-center text-gray-500 mb-4">
              Are you sure you want to reject this return request?
              The items will be sent back to the technician's inventory.
            </p>

            <div className="bg-gray-50 p-3 rounded-md mb-6 w-full">
              <p className="text-sm font-medium text-gray-700 mb-1">Rejection Reason:</p>
              <p className="text-sm text-gray-600 italic">"{rejectionReason}"</p>
            </div>

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                disabled={loading}
              >
                No, Go Back
              </button>
              <button
                type="button"
                onClick={handleFinalRejection}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none flex items-center"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <FiXCircle className="mr-2" />
                    Yes, Reject
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default RejectReturnModal;